"use client";

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { ChatMessage } from "@/lib/api";

interface ChatSessionState {
  /** Last user-id seen on a hydrate-or-bind call. Used to ensure the
   *  persisted transcript is scoped to the current user — when a
   *  different user signs in, we wipe the in-memory + persisted
   *  state so the previous user's transcript can never leak. */
  userId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
  lastAgentName?: string;
  lastAgentColor?: string;
  appendMessage: (message: ChatMessage) => void;
  setLoading: (isLoading: boolean) => void;
  setLastAgent: (agent?: { name?: string; color?: string }) => void;
  clear: () => void;
  /** Called from a top-level boundary (e.g. ChatScreen) on every
   *  render with the current auth user id. If the persisted store
   *  belongs to a different user, the transcript is reset. */
  bindUser: (userId: string | null) => void;
}

/**
 * codex follow-up 2026-05-25 — same-session chat transcript
 * persistence. Strict UAT reported 5 user_isolation_failures that
 * were actually same-user transcript-reload misses, not cross-user
 * leakage. The fix: persist this store to **sessionStorage** (not
 * localStorage — sessionStorage is per-tab and clears on tab close)
 * scoped by user id. Same-user reload restores transcript. A
 * different user logging in triggers `bindUser` with a new id,
 * which wipes the persisted state before any rendering. So:
 *   - same user, same tab, reload → transcript restored
 *   - logout + login as same user → transcript restored
 *   - logout + login as DIFFERENT user → transcript cleared
 *   - new tab → empty transcript (sessionStorage is tab-scoped)
 *
 * The persist middleware uses `name: "decisionease-chat-session"` as
 * the sessionStorage key. We deliberately do NOT persist `isLoading`
 * (transient UI state).
 */
export const useChatSessionStore = create<ChatSessionState>()(
  persist(
    (set, get) => ({
      userId: null,
      messages: [],
      isLoading: false,
      lastAgentName: undefined,
      lastAgentColor: undefined,
      appendMessage: (message) =>
        set((state) => ({ messages: [...state.messages, message] })),
      setLoading: (isLoading) => set({ isLoading }),
      setLastAgent: (agent) =>
        set({
          lastAgentName: agent?.name,
          lastAgentColor: agent?.color,
        }),
      clear: () =>
        set({
          messages: [],
          isLoading: false,
          lastAgentName: undefined,
          lastAgentColor: undefined,
        }),
      bindUser: (userId) => {
        const prev = get().userId;
        // First bind (after refresh): adopt the user id but keep the
        // persisted transcript — same-user reload should restore it.
        if (prev === null && userId !== null) {
          set({ userId });
          return;
        }
        // Different user signed in: wipe everything immediately so
        // their transcript / agent context / loading state can't
        // overlap.
        if (prev !== null && userId !== null && prev !== userId) {
          set({
            userId,
            messages: [],
            isLoading: false,
            lastAgentName: undefined,
            lastAgentColor: undefined,
          });
          return;
        }
        // Logged out: clear transcript so the next login doesn't
        // momentarily show the previous one.
        if (prev !== null && userId === null) {
          set({
            userId: null,
            messages: [],
            isLoading: false,
            lastAgentName: undefined,
            lastAgentColor: undefined,
          });
        }
      },
    }),
    {
      name: "decisionease-chat-session",
      storage: createJSONStorage(() =>
        typeof window !== "undefined" ? window.sessionStorage : (undefined as unknown as Storage)
      ),
      // Don't restore loading state across reloads.
      partialize: (state) => ({
        userId: state.userId,
        messages: state.messages,
        lastAgentName: state.lastAgentName,
        lastAgentColor: state.lastAgentColor,
      }),
    },
  ),
);
